import { useMemo } from 'react';

export const NOMBRES_MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export const getNombreMes = (mesNum) => {
    if (!mesNum || mesNum < 1 || mesNum > 12) return '';
    return NOMBRES_MESES[mesNum - 1];
};

export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

/**
 * Hook personalizado que centraliza toda la lógica de cálculo financiero,
 * variaciones MoM / QoQ, diagnóstico de constancia y formateo de series para la gráfica.
 */
export const useDistribuidorCalculos = ({ resumen = {}, historico = [], mesesRango = 6 }) => {
    return useMemo(() => {
        const mesActualInfo = historico.find((h) => h.iOrden === 6);
        const etiquetaMesActual = mesActualInfo
            ? `${getNombreMes(mesActualInfo.iMes)} ${mesActualInfo.iAnio}`
            : 'Mes Actual';

        const mesAnteriorInfo = historico.find((h) => h.iOrden === 5);
        const etiquetaMesAnterior = mesAnteriorInfo
            ? `${getNombreMes(mesAnteriorInfo.iMes).substring(0, 3)}`
            : 'mes anterior';

        const totalMesActual = Number(resumen.dTotalMesActual ?? (mesActualInfo?.dTotalDinero ?? 0));
        const totalMesAnterior = Number(resumen.dTotalMesAnterior ?? (mesAnteriorInfo?.dTotalDinero ?? 0));
        const totalMesAntepasado = Number(
            resumen.dTotalMesAntepasado ?? (historico.find((h) => h.iOrden === 4)?.dTotalDinero ?? 0)
        );

        const promedioUltimos3 = Number(resumen.dPromedioUltimos3Meses ?? 0);
        const promedioAnteriores3 = Number(resumen.dPromedio3MesesAnteriores ?? 0);

        // 1. Variación Mes a Mes (MoM: Mes Actual vs Mes Anterior)
        let variacionMoM = 0;
        if (totalMesAnterior > 0) {
            variacionMoM = ((totalMesActual - totalMesAnterior) / totalMesAnterior) * 100;
        } else if (totalMesActual > 0) {
            variacionMoM = 100;
        }
        const esMoMPositivo = variacionMoM >= 0;

        // 2. Variación Trimestral (QoQ: Últimos 3 meses vs 3 meses anteriores)
        let variacionQoQ = 0;
        if (promedioAnteriores3 > 0) {
            variacionQoQ = ((promedioUltimos3 - promedioAnteriores3) / promedioAnteriores3) * 100;
        } else if (promedioUltimos3 > 0) {
            variacionQoQ = 100;
        }
        const esQoQPositivo = variacionQoQ >= 0;

        // 3. Diagnóstico de Salud / Constancia del Distribuidor
        const mesesConCompra = [totalMesAntepasado, totalMesAnterior, totalMesActual].filter((v) => v > 0).length;

        let diagnostico = {
            texto: 'Compras constantes',
            colorClass: 'badge-subtle-success text-success',
            esPositivo: true,
            detalle: `${mesesConCompra} de 3 meses activos`
        };

        if (totalMesActual === 0 && (totalMesAnterior > 0 || totalMesAntepasado > 0)) {
            diagnostico = {
                texto: 'Sin compras este mes',
                colorClass: 'badge-subtle-danger text-danger',
                esPositivo: false,
                detalle: 'Riesgo de abandono del distribuidor'
            };
        } else if (
            totalMesAntepasado > 0 &&
            totalMesAnterior > 0 &&
            totalMesActual > 0 &&
            totalMesAntepasado > totalMesAnterior &&
            totalMesAnterior > totalMesActual
        ) {
            diagnostico = {
                texto: 'En declive (2 meses a la baja)',
                colorClass: 'badge-subtle-warning text-warning',
                esPositivo: false,
                detalle: 'Ventas disminuyendo mes con mes'
            };
        } else if (
            totalMesAntepasado > 0 &&
            totalMesAnterior > 0 &&
            totalMesActual > 0 &&
            totalMesAntepasado < totalMesAnterior &&
            totalMesAnterior < totalMesActual
        ) {
            diagnostico = {
                texto: 'Crecimiento constante',
                colorClass: 'badge-subtle-success text-success',
                esPositivo: true,
                detalle: '2 meses consecutivos al alza'
            };
        } else if (totalMesAnterior > 0 && totalMesActual > totalMesAnterior) {
            diagnostico = {
                texto: 'Recuperando compras',
                colorClass: 'badge-subtle-info text-info',
                esPositivo: true,
                detalle: `+${variacionMoM.toFixed(1)}% vs ${etiquetaMesAnterior}`
            };
        } else if (mesesConCompra === 3) {
            if (totalMesAnterior > 0 && totalMesActual < totalMesAnterior) {
                diagnostico = {
                    texto: 'A la baja este mes',
                    colorClass: 'badge-subtle-warning text-warning',
                    esPositivo: false,
                    detalle: `${variacionMoM.toFixed(1)}% vs ${etiquetaMesAnterior}`
                };
            } else {
                diagnostico = {
                    texto: 'Distribuidor constante',
                    colorClass: 'badge-subtle-primary text-primary',
                    esPositivo: true,
                    detalle: '3 de 3 meses con compras'
                };
            }
        } else if (mesesConCompra === 0) {
            diagnostico = {
                texto: 'Sin compras',
                colorClass: 'badge-subtle-secondary text-secondary',
                esPositivo: false,
                detalle: 'Sin actividad en el trimestre'
            };
        } else {
            diagnostico = {
                texto: 'Compras irregulares',
                colorClass: 'badge-subtle-warning text-warning',
                esPositivo: false,
                detalle: `${mesesConCompra} de 3 meses con compra`
            };
        }

        // 4. Preparación de series para la gráfica
        const datosFiltrados = mesesRango === 3 ? historico.slice(-3) : historico;

        const categories = datosFiltrados.map((item) => {
            return `${getNombreMes(item.iMes).substring(0, 3)} ${item.iAnio}`;
        });

        const totalMesValues = datosFiltrados.map((item) => Number((item.dTotalDinero ?? 0).toFixed(2)));

        const promedioBloqueValues = datosFiltrados.map((item) => {
            const prom = item.iOrden >= 4 ? promedioUltimos3 : promedioAnteriores3;
            return Number(Number(prom ?? 0).toFixed(2));
        });

        return {
            mesActualInfo,
            etiquetaMesActual,
            mesAnteriorInfo,
            etiquetaMesAnterior,
            totalMesActual,
            totalMesAnterior,
            totalMesAntepasado,
            promedioUltimos3,
            promedioAnteriores3,
            variacionMoM,
            esMoMPositivo,
            variacionQoQ,
            esQoQPositivo,
            mesesConCompra,
            diagnostico,
            datosFiltrados,
            categories,
            totalMesValues,
            promedioBloqueValues
        };
    }, [resumen, historico, mesesRango]);
};

export default useDistribuidorCalculos;
